import { ChangeEvent, memo, useState } from 'react';

import { faEllipsis, faEye, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { faPencil } from '@fortawesome/pro-thin-svg-icons';
import { Button as KendoButton } from '@progress/kendo-react-buttons';
import dayjs from 'dayjs';
import { OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { styled } from 'styled-components';

import { FileModel } from 'models';

import { useBoolean, useEvent } from 'core/hooks';
import { Button, ButtonVariants, Icon, Input } from 'core/ui';

import { useMutationFiles } from 'features/api/hooks/useMutationFiles';
import { AuthenticationScheme, useAuthentication } from 'features/auth';

type FileCardProps = {
  file: FileModel;
  disableActions: boolean;
  onViewFile: (fileId: number) => void;
};

export const UploadWindowFileCard = memo<FileCardProps>(({ file, disableActions, onViewFile }) => {
  if (file.examId == null || file.examId === 0) throw new Error('File must have an examId.');

  const { activeScheme } = useAuthentication();

  const { renameFileMutation, deleteFileMutation } = useMutationFiles(file.examId);

  const [editState, setEditState] = useState<'viewing' | 'renaming'>('viewing');
  const [renameValue, setRenameValue] = useState(file.fileName ?? '');
  const [actionsOpen, { toggle: toggleActionsOpen }] = useBoolean(false);

  const handleRenameClick = useEvent(() => {
    setEditState('renaming');
  });

  const handleDeleteClick = useEvent(async () => {
    if (deleteFileMutation.isPending) return;

    await deleteFileMutation.mutateAsync(file.id);
  });

  const handleViewFileClick = useEvent(() => {
    onViewFile(file.id);
  });

  const handleRenameSaveClick = useEvent(async () => {
    if (renameFileMutation.isPending) return;

    const newFile = { ...file, fileName: renameValue };
    await renameFileMutation.mutateAsync(newFile);
    setEditState('viewing');
  });

  const handleRenameCancelClick = useEvent(() => {
    setEditState('viewing');
  });

  const handleRenameInputChange = useEvent((e: ChangeEvent<HTMLInputElement>) => {
    setRenameValue(e.target.value);
  });

  const handleTitleClick = useEvent(() => {
    // Only allow renaming if the user is a normal user - specifically not a share user.
    if (activeScheme === AuthenticationScheme.OIDC) {
      setEditState('renaming');
    }
  });

  return (
    <Card>
      <StyledCardBody>
        <StyledFileNameTitleContainer>
          {editState === 'renaming' ? (
            <div>
              <Input type="text" value={renameValue} onChange={handleRenameInputChange} autoFocus />
              <StyledRenameContainer>
                <Button onClick={handleRenameSaveClick}>Rename</Button>
                <Button variant={ButtonVariants.SECONDARY} onClick={handleRenameCancelClick}>
                  Cancel
                </Button>
              </StyledRenameContainer>
            </div>
          ) : deleteFileMutation.isPending ? (
            <>
              <Spinner size="sm" />
              &nbsp;<span>Deleting...</span>
            </>
          ) : (
            <Card.Title onClick={handleTitleClick}>{file.fileName ?? 'Unnamed File'}</Card.Title>
          )}
          {file.created && <Card.Text>{`Added ${formatDateString(file.created)}`}</Card.Text>}
        </StyledFileNameTitleContainer>
        <StyledCardBodyActionContainer>
          <KendoButton fillMode="link" type="button" disabled={disableActions} onClick={handleViewFileClick}>
            <StyledIcon icon={faEye} />
          </KendoButton>
          {activeScheme === AuthenticationScheme.OIDC && (
            <OverlayTrigger
              rootClose={true}
              trigger="click"
              placement="bottom-end"
              overlay={
                <StyledPopover id={`popover-${file.id}`}>
                  <Popover.Body>
                    <EditPopoverContainer>
                      <KendoButton fillMode="link" onClick={handleRenameClick}>
                        Rename
                        <StyledIcon icon={faPencil}></StyledIcon>
                      </KendoButton>
                      <KendoButton fillMode="link" onClick={handleDeleteClick}>
                        Delete
                        <StyledDeleteIcon icon={faTrash}></StyledDeleteIcon>
                      </KendoButton>
                    </EditPopoverContainer>
                  </Popover.Body>
                </StyledPopover>
              }
              onToggle={toggleActionsOpen}
              show={actionsOpen}
            >
              <KendoButton fillMode="link" type="button" disabled={disableActions}>
                <StyledIcon icon={faEllipsis}></StyledIcon>
              </KendoButton>
            </OverlayTrigger>
          )}
        </StyledCardBodyActionContainer>
      </StyledCardBody>
    </Card>
  );
});

UploadWindowFileCard.displayName = 'UploadWindowFileCard';

function formatDateString(dateString: string) {
  return dayjs(dateString).format('MM-DD-YYYY hh:mm A');
}

const StyledCardBodyActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledCardBody = styled(Card.Body)`
  display: flex;
  justify-content: space-between;
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.primary};
`;

const StyledPopover = styled(Popover)`
  z-index: 10500;
  width: 150px;
`;

const EditPopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .k-button-text {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;
  }
`;

const StyledDeleteIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.palette.reds[3]};
`;

const StyledRenameContainer = styled.div`
  display: flex;
  gap: 10px;
  padding-top: 10px;
`;

const StyledFileNameTitleContainer = styled.div`
  width: 75%;
`;
